export enum BypassTab {
  custom = 'Custom',
  default = 'Default',
  none = '',
}

export enum BypassType {
  app = 'app',
  url = 'url',
}

export enum TrafficType {
  IGNORE = 'Do Not Bypass',
  BYPASS = 'Bypass',
}

export enum DefaultBypassState {
  APPLIED = 'applied',
  IGNORED = 'ignored',
}

export enum ApplicationOSType {
  MAC = 'Mac',
  WINDOWS = 'Windows',
}

export type ByPassDataTypes = {
  bypassType: string
  name: string
  traffic?: string
  os?: string
  editMode?: boolean
  isEditedOrAdded?: boolean
  isNotSaved?: boolean
  oldName?: string
  isNewlyAdded?: boolean
  type: BypassTab
  updatedBy?: string
  updatedAt?: string
  note?: string
}

export interface CustomDomainBypassListItemFromServer {
  name: string
  note?: string
  updatedBy?: string
  updatedAt?: string
}

export interface CustomDomainBypassListItemOnClient
  extends CustomDomainBypassListItemFromServer {
  isEdit: boolean
  bypassType?: BypassType
  isDefault?: boolean // Whether the bypass is from the default list
}

export type CustomApplicationBypassListItemFromServer = {
  name: string
  note?: string
  updatedBy?: string
  updatedAt?: string
}

export type SingleCustomApplicationBypassListItemOnClient =
  CustomApplicationBypassListItemFromServer & {
    isEdit: boolean
    bypassType?: BypassType
    os?: ApplicationOSType
    isDefault?: boolean // Whether the bypass is from the default list
  }

export interface CustomApplicationBypassDataFromServer {
  Mac: CustomApplicationBypassListItemFromServer[]
  Windows: CustomApplicationBypassListItemFromServer[]
}

export interface DefaultBypassItemList {
  name: string
  bypassType?: BypassType
  traffic?: TrafficType
  isEdit?: boolean
  os?: ApplicationOSType
  isDefault?: boolean // Whether the bypass is from the default list
}

interface ApplicationBypassType {
  Mac: string[]
  Windows: string[]
}

export interface TrackDefaultTabStateForBypasses {
  isDefaultURLBypassTabActive: boolean
  isDefaultApplicationBypassTabActive: boolean
}

export interface BypassDataServer {
  customDomainBypassList: CustomDomainBypassListItemFromServer[]
  customApplicationBypassList: CustomApplicationBypassDataFromServer
  applicationBypassList?: ApplicationBypassType
  applicationDefaultBypassList: ApplicationBypassType
  applicationIgnoreBypassList: ApplicationBypassType
  domainBypassList?: string[]
  domainDefaultBypassList: string[]
  domainIgnoreBypassList: string[]
  inheritsApplicationBypass: boolean
  inheritsDomainBypass: boolean
}

export type BypassDataServerRequest = Omit<
  BypassDataServer,
  'domainDefaultBypassList' | 'applicationDefaultBypassList'
>

export interface DefaultBypassApiItem {
  name: string
  state: DefaultBypassState
}

export interface CustomBypassApiItem {
  name: string
  note?: string
}

export type BypassUpdateResource = 'url' | 'application'

export type UrlBypassMutation =
  | {
      type: 'upsert-custom'
      custom?: CustomBypassApiItem[]
      default?: DefaultBypassApiItem[]
      deleteCustomUrls?: string[]
    }
  | {
      type: 'update-default'
      custom?: CustomBypassApiItem[]
      default: DefaultBypassApiItem[]
    }
  | {
      type: 'reset-to-base'
    }
  | {
      type: 'delete-custom'
      urls: string[]
      default: DefaultBypassApiItem[]
      isInherited: true
    }
  | {
      type: 'delete-custom'
      urls: string[]
      default?: never
      isInherited?: false
    }

export type ApplicationBypassMutation =
  | {
      type: 'upsert-custom'
      custom?: ApplicationBypassApiPlatformUpdate<CustomBypassApiItem>
      default?: ApplicationBypassApiPlatformUpdate<DefaultBypassApiItem>
      deleteCustom?: ApplicationBypassApiPlatformUpdate<string>
    }
  | {
      type: 'update-default'
      custom?: ApplicationBypassApiPlatformUpdate<CustomBypassApiItem>
      default: ApplicationBypassApiPlatformUpdate<DefaultBypassApiItem>
    }
  | {
      type: 'reset-to-base'
    }
  | {
      type: 'delete-custom'
      custom: ApplicationBypassApiPlatformUpdate<string>
    }

export type UrlBypassUpdateData =
  | {
      inheritsFromBase: true
      custom?: never
      default?: never
    }
  | {
      inheritsFromBase?: never
      custom: CustomBypassApiItem[]
      default?: DefaultBypassApiItem[]
    }
  | {
      inheritsFromBase?: never
      custom?: CustomBypassApiItem[]
      default: DefaultBypassApiItem[]
    }

export interface UrlBypassData {
  inheritsFromBase: boolean
  custom: CustomDomainBypassListItemFromServer[]
  default: DefaultBypassApiItem[]
}

export interface GetUrlBypassResponse {
  data: UrlBypassData
}

export interface UpdateUrlBypassPayload {
  data: UrlBypassUpdateData
}

export interface DeleteUrlBypassPayload {
  data: {
    custom: {
      urls: string[]
    }
  }
}

export interface ApplicationBypassApiPlatformLists<T> {
  mac: T[]
  windows: T[]
}

export type ApplicationBypassApiPlatformUpdate<T> =
  | {
      mac: T[]
      windows?: T[]
    }
  | {
      mac?: T[]
      windows: T[]
    }

export type ApplicationBypassUpdateData =
  | {
      inheritsFromBase: true
      custom?: never
      default?: never
    }
  | {
      inheritsFromBase?: never
      custom: ApplicationBypassApiPlatformUpdate<CustomBypassApiItem>
      default?: ApplicationBypassApiPlatformUpdate<DefaultBypassApiItem>
    }
  | {
      inheritsFromBase?: never
      custom?: ApplicationBypassApiPlatformUpdate<CustomBypassApiItem>
      default: ApplicationBypassApiPlatformUpdate<DefaultBypassApiItem>
    }

export interface ApplicationBypassData {
  inheritsFromBase: boolean
  custom: ApplicationBypassApiPlatformLists<CustomApplicationBypassListItemFromServer>
  default: ApplicationBypassApiPlatformLists<DefaultBypassApiItem>
}

export interface GetApplicationBypassResponse {
  data: ApplicationBypassData
}

export interface UpdateApplicationBypassPayload {
  data: ApplicationBypassUpdateData
}

export interface DeleteApplicationBypassPayload {
  data: {
    custom: ApplicationBypassApiPlatformUpdate<string>
  }
}

export interface AddCustomBypassToAllPoliciesPayload {
  data: {
    urls?: CustomBypassApiItem[]
    applications?: {
      mac?: CustomBypassApiItem[]
      windows?: CustomBypassApiItem[]
    }
  }
}

export interface BypassDataClient
  extends Omit<
    BypassDataServer,
    | 'customApplicationBypassList'
    | 'customDomainBypassList'
    | 'domainDefaultBypassList'
    | 'applicationDefaultBypassList'
    | 'applicationIgnoreBypassList'
  > {
  customDomainBypassList: CustomDomainBypassListItemOnClient[]
  domainDefaultBypassList: DefaultBypassItemList[]
  customApplicationBypassList: SingleCustomApplicationBypassListItemOnClient[]
  applicationDefaultBypassList: DefaultBypassItemList[]
  applicationIgnoreBypassList: DefaultBypassItemList[]
}

export type DeleteBypassDataType = {name: string; type: string}

export type OneOfClientOnlyDomainBypassKey =
  | 'isEdit'
  | 'bypassType'
  | 'isDefault'

export type OneOfClientOnlyAppBypassKey =
  | 'isEdit'
  | 'bypassType'
  | 'isDefault'
  | 'os'

export interface RenderedURLBypassListItem {
  name: string
  note?: string
  updatedBy?: string
  updatedAt?: string
  isEdit?: boolean
  traffic?: TrafficType
  isDefault?: boolean
}

export interface RenderedApplicationBypassListItem {
  name: string
  note?: string
  updatedBy?: string
  updatedAt?: string
  isEdit?: boolean
  traffic?: TrafficType
  os?: ApplicationOSType
  isDefault?: boolean
}
